class FormulaEditorialHero {
  constructor(root) {
    this.root = root;
    this.section = root.closest(".formula-editorial-hero");
    this.slides = [...root.querySelectorAll("[data-formula-campaign-slide]")];
    this.contents = [...this.section.querySelectorAll("[data-formula-campaign-content]")];
    this.controls = [...this.section.querySelectorAll("[data-formula-campaign-control]")];
    this.toggle = this.section.querySelector("[data-formula-campaign-toggle]");
    this.toggleLabel = this.section.querySelector("[data-formula-campaign-toggle-label]");
    this.pauseLabel = this.toggle?.dataset.formulaCampaignPauseLabel || "Pause slideshow";
    this.playLabel = this.toggle?.dataset.formulaCampaignPlayLabel || "Play slideshow";
    this.liveRegion = this.section.querySelector(".formula-editorial-hero__content-wrap");
    this.autoplayEnabled = root.dataset.formulaCampaignAutoplay === "true";
    this.autoplayDelay = Number(root.dataset.formulaCampaignDelay) || 6000;
    this.autoplayTimer = null;
    this.autoplayPaused = false;
    this.pausedBeforeSelect = undefined;
    this.touchStartX = null;
    this.touchStartY = null;
    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (this.toggle) {
      this.toggle.addEventListener("click", () => this.setPaused(!this.autoplayPaused));
      /*
       * With motion suppressed the timer never runs, so a control offering to
       * pause it would be a lie. Drop it rather than leave a dead button.
       */
      if (!this.autoplayEnabled || this.slides.length < 2 || this.motionQuery.matches) {
        this.toggle.remove();
        this.toggle = null;
      }
    }

    this.controls.forEach((control, index) => {
      control.addEventListener("click", () => {
        this.setPaused(true);
        this.show(Number(control.dataset.formulaCampaignControl));
      });

      control.addEventListener("keydown", (event) => {
        const lastIndex = this.controls.length - 1;
        let nextIndex = index;

        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          nextIndex = index === lastIndex ? 0 : index + 1;
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          nextIndex = index === 0 ? lastIndex : index - 1;
        } else if (event.key === "Home") {
          nextIndex = 0;
        } else if (event.key === "End") {
          nextIndex = lastIndex;
        } else {
          return;
        }

        event.preventDefault();
        this.setPaused(true);
        this.show(nextIndex);
        this.controls[nextIndex].focus();
      });
    });

    this.section.addEventListener("mouseenter", () => this.stopAutoplay());
    this.section.addEventListener("mouseleave", () => this.startAutoplay());
    this.section.addEventListener("focusin", () => this.stopAutoplay());
    this.section.addEventListener("focusout", (event) => {
      if (event.relatedTarget instanceof Node && this.section.contains(event.relatedTarget)) return;
      this.startAutoplay();
    });
    this.section.addEventListener("touchstart", (event) => this.startSwipe(event), { passive: true });
    this.section.addEventListener("touchend", (event) => this.endSwipe(event), { passive: true });
    this.section.addEventListener("touchcancel", () => this.resetSwipe(), { passive: true });
    this.section.addEventListener("shopify:block:select", (event) => this.selectBlock(event));
    this.section.addEventListener("shopify:block:deselect", (event) => this.deselectBlock(event));

    this.startAutoplay();
  }

  /*
   * Theme Editor: selecting a Slide block brings that slide forward and holds
   * it there while it is edited; deselecting restores the previous state.
   */
  selectBlock(event) {
    const slide = event.target.closest?.("[data-formula-campaign-slide]");
    const index = this.slides.indexOf(slide);
    if (index < 0) return;

    if (this.pausedBeforeSelect === undefined) {
      this.pausedBeforeSelect = this.autoplayPaused;
    }
    this.setPaused(true);
    this.show(index);
  }

  deselectBlock(event) {
    const slide = event.target.closest?.("[data-formula-campaign-slide]");
    if (!this.slides.includes(slide) || this.pausedBeforeSelect === undefined) return;

    const wasPaused = this.pausedBeforeSelect;
    this.pausedBeforeSelect = undefined;
    this.setPaused(wasPaused);
  }

  startSwipe(event) {
    if (event.touches.length !== 1 || this.slides.length < 2) return;

    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  }

  endSwipe(event) {
    if (this.touchStartX === null || this.touchStartY === null || event.changedTouches.length !== 1) {
      this.resetSwipe();
      return;
    }

    const horizontalDistance = event.changedTouches[0].clientX - this.touchStartX;
    const verticalDistance = event.changedTouches[0].clientY - this.touchStartY;
    this.resetSwipe();

    if (Math.abs(horizontalDistance) < 50 || Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) return;

    const activeIndex = this.slides.findIndex((slide) => slide.classList.contains("is-active"));
    const currentIndex = activeIndex < 0 ? 0 : activeIndex;
    const direction = horizontalDistance < 0 ? 1 : -1;
    const nextIndex = (currentIndex + direction + this.slides.length) % this.slides.length;

    this.setPaused(true);
    this.show(nextIndex);
  }

  resetSwipe() {
    this.touchStartX = null;
    this.touchStartY = null;
  }

  show(activeIndex) {
    if (activeIndex < 0 || activeIndex >= this.slides.length) return;

    this.slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.classList.toggle("is-active", isActive);
      slide.setAttribute("aria-hidden", String(!isActive));
    });

    this.contents.forEach((content, index) => {
      const isActive = index === activeIndex;
      content.classList.toggle("is-active", isActive);
      content.setAttribute("aria-hidden", String(!isActive));
      content.toggleAttribute("inert", !isActive);
    });

    this.controls.forEach((control, index) => {
      const isActive = index === activeIndex;
      control.classList.toggle("is-active", isActive);
      control.setAttribute("aria-pressed", String(isActive));
    });
  }

  setPaused(paused) {
    this.autoplayPaused = paused;

    if (this.toggle) {
      this.toggle.setAttribute("aria-pressed", String(paused));
      this.toggle.classList.toggle("is-paused", paused);
      if (this.toggleLabel) {
        this.toggleLabel.textContent = paused ? this.playLabel : this.pauseLabel;
      }
    }

    if (paused) {
      this.stopAutoplay();
    } else {
      /*
       * Resuming is always an explicit press of the toggle, which leaves focus
       * (and usually the pointer) inside the section. Honouring the hover and
       * focus guards here would swallow the press, so skip them.
       */
      this.startAutoplay(true);
    }
  }

  startAutoplay(force = false) {
    this.stopAutoplay();

    if (
      !this.autoplayEnabled ||
      this.autoplayPaused ||
      this.slides.length < 2 ||
      this.motionQuery.matches ||
      !this.root.isConnected ||
      (!force && this.section.matches(":hover")) ||
      (!force && this.section.contains(document.activeElement))
    ) {
      return;
    }

    this.liveRegion?.setAttribute("aria-live", "off");
    this.autoplayTimer = window.setTimeout(() => {
      const activeIndex = this.slides.findIndex((slide) => slide.classList.contains("is-active"));
      const nextIndex = activeIndex < 0 ? 0 : (activeIndex + 1) % this.slides.length;
      this.show(nextIndex);
      this.startAutoplay(force);
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
    this.liveRegion?.setAttribute("aria-live", "polite");
  }
}

function initFormulaEditorialHeroes(scope = document) {
  scope.querySelectorAll("[data-formula-campaign]").forEach((root) => {
    if (root.dataset.formulaCampaignReady === "true") return;
    root.dataset.formulaCampaignReady = "true";
    new FormulaEditorialHero(root);
  });
}

document.addEventListener("DOMContentLoaded", () => initFormulaEditorialHeroes());
document.addEventListener("shopify:section:load", (event) => initFormulaEditorialHeroes(event.target));
